'use strict'

const values_ = require('lodash/values')
const includes_ = require('lodash/includes')
const merge_ = require('lodash/merge')
const negate_ = require('lodash/negate')

const createDatasetController = require('../dataset-controller/controller')
const DATASET_TYPES = require('dbsm-common/src/datasetTypes')
const sendBiOnError = require('dbsm-common/src/bi/sendBiOnError')
const addRavenData = require('dbsm-common/src/raven/addRavenData')
const schemaAPICreator = require('./schemaAPI')
const wixDataProxyCreator = require('../wix-data/wixDataProxy')
const { isDatasetError } = require('../dataset-controller/DatasetError')
const errorBoundariesCreator = require('../error-boundaries/errorBoundaries')
const createRavenProxy = require('../raven/ravenProxy')
const createFedopsLogger = require('fedops-logger/dist/src/logger-factory')
  .create
const createRavenHooksForFedops = require('dbsm-common/src/raven/createRavenHooksForFedops')

const extractRouterPayload = (payload, parser) => ({
  items: parser(payload.items),
  totalCount: payload.totalCount
})

const mergeRouterDatasetConfig = (routerConfig, controllerConfig) =>
  merge_({}, controllerConfig, routerConfig)

module.exports = ({
  wixDataCreator,
  wixSdk,
  elementorySupport,
  errorReporter,
  Raven,
  UserRaven,
  biLogger
}) => {
  const { startHook, endHook } = createRavenHooksForFedops(Raven)
  // custom timeoutHook for viewer-app fedops logger. Logs with level info until stabilized, to prevent false alarms
  const timeoutHook = ({ name, timeout }) =>
    Raven.captureMessage(`interaction ${name} timed out`, {
      level: 'info',
      extra: {
        interactionTimeout: timeout
      }
    })
  const fedopsLogger = createFedopsLogger('wix-code-dbsm-viewer-app', {
    interactionTimeout: 50000,
    startHook,
    endHook,
    timeoutHook
  })

  const ravenProxy = createRavenProxy(Raven)
  const userRavenProxy = createRavenProxy(UserRaven)
  const errorBoundaries = errorBoundariesCreator({
    Raven: ravenProxy,
    UserRaven: userRavenProxy
  })

  let wixDataProxy
  let wixDataSchemasProxy
  let routerPayload
  let platformUtilities
  let schemaAPI

  sendBiOnError(biLogger, Raven, negate_(isDatasetError))

  return {
    initAppForPage: ({ routerReturnedData }, platformUtils) => {
      const { timeoutId } = fedopsLogger.interactionStarted('initAppForPage')
      addRavenData(Raven, routerReturnedData, 'routerReturnedData')
      try {
        ;({ wixDataProxy, wixDataSchemasProxy } = wixDataProxyCreator(
          errorBoundaries.wixDataCodeZone,
          wixDataCreator
        ))
        routerPayload = routerReturnedData || {}
        platformUtilities = platformUtils
        schemaAPI = schemaAPICreator(wixDataSchemasProxy, Raven)
        fedopsLogger.interactionEnded('initAppForPage', { timeoutId })
        return Promise.resolve()
      } catch (e) {
        Raven.captureException(e)
        return Promise.reject(e)
      }
    },

    createControllers: controllerConfigs => {
      try {
        const {
          timeoutId: createControllersTimeoutId
        } = fedopsLogger.interactionStarted('createControllers')
        if (controllerConfigs.length) {
          const {
            timeoutId: loadSchemasTimeoutId
          } = fedopsLogger.interactionStarted('pageReady/loadSchemas')
          schemaAPI
            .loadSchemas()
            .then(() => {
              fedopsLogger.interactionEnded('pageReady/loadSchemas', {
                timeoutId: loadSchemasTimeoutId
              })
            })
            .catch(e => e)
        }

        const datasetTypes = values_(DATASET_TYPES)
        const controllers = controllerConfigs.map(
          ({ type, config, connections, $w }, controllerIndex) => {
            if (!includes_(datasetTypes, type)) {
              throw new Error(
                `type of controller MUST be one of ${datasetTypes} but is ${type}`
              )
            }
            const uniqueId = `${controllerIndex}_${type}`
            const datasetConfiguration =
              type === DATASET_TYPES.ROUTER_DATASET
                ? mergeRouterDatasetConfig(routerPayload.config, config)
                : config
            const prefetchedData =
              type === DATASET_TYPES.ROUTER_DATASET
                ? extractRouterPayload(
                    routerPayload,
                    elementorySupport.wireFormatParser
                  )
                : null

            const datasetController = createDatasetController({
              controllerConfig: datasetConfiguration,
              connections,
              wixDataProxy,
              wixDataSchemasProxy,
              firePlatformEvent: errorBoundaries.userCodeZone($w.fireEvent),
              errorBoundaries,
              wixSdk,
              errorReporter,
              platformUtilities,
              prefetchedData,
              ravenProxy,
              uniqueId,
              schemaAPI,
              fedopsLogger
            })
            return Promise.resolve(datasetController)
          }
        )
        fedopsLogger.interactionEnded('createControllers', {
          timeoutId: createControllersTimeoutId
        })
        return controllers
      } catch (e) {
        Raven.captureException(e)
        return []
      }
    }
  }
}



// WEBPACK FOOTER //
// ./viewer-app-module/appCreator.js